import { format } from 'date-fns-tz';
import { saveAs } from 'file-saver';
import { toBlob } from 'html-to-image';
import React, { type RefObject } from 'react';
import { useReactToPrint } from 'react-to-print';
import { Tooltip } from 'react-tooltip';

import { ReactComponent as ChartPrintIcon } from '@assets/svg/chart-icons/ic-chartPrint.svg';
import { ReactComponent as DownloadIcon } from '@assets/svg/ic-chart-download.svg';

import useTranslation from '@hooks/useTranslation';

import type { IChartSeriesItem } from '@model/chartType';
import type { IInstallationPoint } from '@model/installationPoint';

import { useChartExportMutation } from '../../services/useChartExportMutation';
import { useChartFftExportMutation } from '../../services/useChartFftExportMutation';
import style from './style.module.scss';

interface IProps {
  id: string;
  point: IInstallationPoint;
  printRef: RefObject<Element | Text>;
  dateTimeFormat: string;
  chartTypeLabel: string;
  chartTitle: string;
  chartSeries?: Array<IChartSeriesItem>;
}

interface IContent {
  readingId: number;
  timeValue?: number;
  fftChartType?: number;
  axisId?: number;
  isImpactVue?: boolean;
}

interface IExportDataParams {
  readingTypeId: number;
  installationPointId: number;
  from?: number;
  range?: number;
}

const DownloadContextMenu: React.FC<IProps> = ({
  id,
  point,
  printRef,
  dateTimeFormat,
  chartTypeLabel,
  chartTitle,
  chartSeries,
}) => {
  const { t } = useTranslation();

  const { mutate: chartExportMutate } = useChartExportMutation();
  const { mutate: chartFftExportMutate } = useChartFftExportMutation();

  const handlePrint = useReactToPrint({
    documentTitle: t(chartTypeLabel),
    contentRef: printRef,
    preserveAfterPrint: true,
  });

  const handleClickDownloadPng = async () => {
    if (printRef?.current) {
      //@ts-ignore
      const imageBlob = await toBlob(printRef.current);

      //@ts-ignore
      const imageUrl = URL.createObjectURL(imageBlob);
      const img = new Image();
      img.src = imageUrl;

      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = img.width;
        canvas.height = img.height + 20;

        if (ctx) {
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, canvas.width, canvas.height);

          ctx.drawImage(img, 10, 25);

          ctx.font = '20px Roboto';
          ctx.fillStyle = 'black';
          ctx.fillText(chartTitle, 25, 25);
        }

        canvas.toBlob((finalBlob) => {
          //@ts-ignore
          saveAs(finalBlob, `${point.name} - ${chartTypeLabel} - ${format(new Date(), dateTimeFormat)}.png`);
        }, 'image/png');
      };
    }
  };

  const handleClickDownloadCSV = (content: IContent) => {
    if (content.fftChartType) {
      const { fftChartType, readingId, axisId } = content;
      chartFftExportMutate(
        {
          fftChartType,
          readingId,
          isImpactVue: !!axisId && +axisId === 4,
        },
        {
          onSuccess: (result) => {
            const axis = chartSeries?.find((serie) => serie?.axisId === axisId);

            saveAs(
              new Blob([result?.data], { type: 'text/plain;charset=utf-8' }),
              `${point.name} - ${chartTypeLabel} - ${axis?.axisName || ''} - ${format(
                new Date(),
                dateTimeFormat,
              )}.csv`,
            );
          },
        },
      );
    } else {
      const { readingId, timeValue = 14 } = content;

      const exportData: IExportDataParams = {
        readingTypeId: readingId,
        installationPointId: point.id,
      };
      if (point?.point_type === 'adapter') {
        exportData['range'] = timeValue * 24 * 3600 * 1000;
      } else {
        exportData['from'] = Math.round(Date.now() / 1000) - timeValue * 24 * 3600;
      }
      chartExportMutate(exportData, {
        onSuccess: (result) => {
          saveAs(
            new Blob([result?.data], { type: 'text/plain;charset=utf-8' }),
            `${point.name} - ${chartTypeLabel} - ${format(new Date(), dateTimeFormat)}.csv`,
          );
        },
      });
    }
  };

  return (
    <Tooltip
      id={id}
      place="bottom-end"
      openOnClick
      className={style.downloadMenu}
      clickable
      disableStyleInjection
      render={({ content }) => {
        if (!content) {
          return null;
        }
        const chartInfo = JSON.parse(content);
        return (
          <>
            <div className={style.downloadMenuItem} onClick={() => handlePrint()}>
              <ChartPrintIcon className={style.downloadMenuItemIcon} />
              {t('Print')}
            </div>
            <div className={style.downloadMenuItem} onClick={() => handleClickDownloadPng()}>
              <DownloadIcon className={style.downloadMenuItemIcon} />
              {t('Download PNG image')}
            </div>
            <div className={style.downloadMenuItem} onClick={() => handleClickDownloadCSV(chartInfo)}>
              <DownloadIcon className={style.downloadMenuItemIcon} />
              {t('Download CSV')}
            </div>
          </>
        );
      }}
    />
  );
};

export default DownloadContextMenu;
